import {
  PIPELINE_ACTION_ID,
  SMS_ACTION_ID,
  TAGS_ACTION_ID,
  TASK_ACTION_ID,
  WORKFLOW_ACTION_ID,
} from 'app/components/contacts/bulkActionIds'
import type {
  BulkAction,
  BulkApplyResult,
  BulkProgressReporter,
  BulkRowOutcome,
} from 'app/components/table/bulkActions/types'
import type { TreatmentDestination } from 'app/types/treatment'
import type { StageOutcome } from 'app/types/list-board'
import { TREATMENT_STAGES, TreatmentRow } from './useTreatmentBoard'

/** Moves every selected case to one stage. Not a contact field, so its own id. */
export const STAGE_ACTION_ID = `__treatmentStage`

/**
 * Which of the contacts table's bulk actions a treatment table offers.
 *
 * Everything here is something a person does *while chasing a case*: tag the
 * cohort, raise a task, text them, enrol them in a follow-up sequence, drop
 * them on a pipeline. The contacts table's field setters (lead status, the UTM
 * fields, reported attribution, mark as non-patient) are contact admin rather
 * than case work, and a table of outstanding treatment is not where anybody
 * goes to correct a UTM campaign.
 */
const SHARED_ACTION_IDS = [TAGS_ACTION_ID, PIPELINE_ACTION_ID, TASK_ACTION_ID, WORKFLOW_ACTION_ID, SMS_ACTION_ID]

/** One stage write, as `useTreatmentBoard` exposes it, and `useContactBoard` for its contacts. */
export type SetStage<R = TreatmentRow> = (
  cardId: string,
  stage: string,
  known?: R,
  opts?: { quiet?: boolean },
) => Promise<StageOutcome>

/**
 * The treatment table's action menu: the stage move, then whichever of the
 * contacts actions belong on a case.
 *
 * `contactActions` arrives already narrowed to what this user may do (Send SMS
 * behind its toggle, workflows only when one exists to enrol into), and its
 * order is kept rather than re-imposed here, so the shared half of this menu
 * reads the same as the contacts table's.
 */
export function treatmentBulkActionList(
  contactActions: BulkAction[],
  stages: TreatmentDestination[] = TREATMENT_STAGES,
): BulkAction[] {
  const stageAction: BulkAction = {
    id: STAGE_ACTION_ID,
    label: `Set stage`,
    control: `select`,
    // All six, the two exits included: marking a batch scheduled or lost is
    // the whole reason to select several rows at once after a phone round.
    options: stages.map((stage) => ({ label: stage.name, value: stage.name })),
    placeholder: `Choose a stage…`,
  }
  return [stageAction, ...contactActions.filter((action) => SHARED_ACTION_IDS.includes(action.id))]
}

/**
 * Moves each selected case, or contact on a contact board, in turn, reporting as it goes.
 *
 * One request per row, like every other bulk adapter here: the progress bar is
 * real rather than an animation. Sequential on purpose, since each write is an
 * optimistic move on the board underneath and firing them together would race
 * the overlay that draws them.
 */
export async function applyStageChange<R extends { id?: string | null; name?: string | null }>(
  rows: R[],
  stage: string,
  setStage: SetStage<R>,
  onProgress?: BulkProgressReporter,
): Promise<BulkApplyResult> {
  const outcomes: BulkRowOutcome[] = []
  const tally = { ok: 0, failed: 0, skipped: 0 }

  for (const row of rows) {
    const label = row.name || row.id || `Unknown patient`
    // `row` goes along as the known card: a table row can sit outside the page
    // of the band the board is holding, and a move on a case it cannot find
    // would otherwise do nothing at all, silently.
    const outcome = row.id
      ? await setStage(row.id, stage, row, { quiet: true })
      : { ok: false, reason: `Missing an id` }
    const result: BulkRowOutcome = { id: row.id ?? ``, label, ...outcome }
    outcomes.push(result)
    if (result.ok) tally.ok += 1
    else if (result.skipped) tally.skipped += 1
    else tally.failed += 1
    onProgress?.({ done: outcomes.length, total: rows.length, ...tally, last: result })
  }

  return { outcomes }
}
